import { FC, Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PaxCheckHaveDocumentQuestion, PaxCheckQuestionSampleImage } from "api";
import { countryCodesByAlpha3 } from "assets/country-codes/country-codes";

/* components */
import QuestionSampleHaveDocuments from "../question-sample/QuestionSampleHaveDocuments";
import Flag from "features/shared/components/picker/components/Flag";
import LocalImages from "assets/images/local-images";
import Checkbox from "features/shared/components/checkbox/Checkbox";
import { DocSampleBox } from "../question-sample/QuestionSampleHaveDocuments";

/* styles */
import Body from "features/shared/styles/typography/Body.styled";
import FlagRoundFrame from "./styles/FlagRoundFrame.styled";
import StyledQuestionCaption from "./styles/StyledQuestionCaption.styled";
import { ButtonTitle } from "features/shared/styles/typography/ButtonTitle.styled";
import { Summary } from "features/shared/styles/typography/Summary.styled";
import { MobileDocSample, DesktopDocSample } from "./styles/DocSample.styled";
import QuestionBox from "features/questions-page/styles/QuestionBox.styled";
import HaveDocumentQuestion from "./styles/HaveDocumentQuestion.styled";
import { capitalizeTitle } from "utils";

export interface SampleDocument {
  id: string;
  image: PaxCheckQuestionSampleImage | null;
  groupIdx: number;
  questionIdxDesktop: number;
  questionIdxMobile: number;
}

interface HaveDocumentQuestionProps {
  questions: PaxCheckHaveDocumentQuestion[];
  answers: Map<string, boolean | string | undefined>;
  handleChange: (id: string) => (value: boolean) => void;
}

const getImageForDocument = (alpha3Country: string) => {
  const code = countryCodesByAlpha3[alpha3Country];

  if (alpha3Country === "EURO") {
    return (
      <img
        width="36"
        height="36"
        src={LocalImages.shared.images.icons.docEuro}
        alt="passport icon"
      />
    );
  }

  return code && code.alpha2 ? (
    <FlagRoundFrame>
      <Flag codeAlpha2={code.alpha2} direction={"ltr"} />
    </FlagRoundFrame>
  ) : (
    <img
      width="36"
      height="36"
      src={LocalImages.shared.images.icons.docSelectorPassport}
      alt="passport icon"
    />
  );
};

const HaveDocumentQuestionsPane: FC<HaveDocumentQuestionProps> = ({
  questions,
  answers,
  handleChange,
}) => {
  const [sampleDocument, setSampleDocument] = useState<SampleDocument | null>(
    null
  );

  const cardRefs = useRef<Map<string, HTMLDivElement | null>>(new Map());

  // Sort questions by docCategoryOrder
  const sortedQuestions = useMemo(
    () =>
      [...questions].sort((a, b) => a.docCategoryOrder - b.docCategoryOrder),
    [questions]
  );

  // Group questions by docCategoryTitle
  const groupedQuestions = useMemo(
    () =>
      sortedQuestions.reduce<Record<string, PaxCheckHaveDocumentQuestion[]>>(
        (acc, question) => {
          if (!acc[question.docCategoryTitle]) {
            acc[question.docCategoryTitle] = [];
          }
          acc[question.docCategoryTitle].push(question);
          return acc;
        },
        {}
      ),
    [sortedQuestions]
  );

  const getRowEndIndex = useCallback(
    (groupIdx: number, questionIdx: number, questionsCount: number): number => {
      const card = cardRefs.current.get(`${groupIdx}-${questionIdx}`);
      const section = card?.parentElement;
      const cardWidth = card?.offsetWidth ?? 0;
      if (!card || !section || cardWidth === 0) return questionIdx;

      const gap = parseFloat(getComputedStyle(section).columnGap) || 0;
      const columns = Math.max(
        1,
        Math.floor((section.clientWidth + gap) / (cardWidth + gap))
      );

      const rowStart = Math.floor(questionIdx / columns) * columns;
      return Math.min(rowStart + columns - 1, questionsCount - 1);
    },
    []
  );

  // Keep the open sample in the correct row when the layout reflows (resize
  // changes how many cards fit per row).
  useEffect(() => {
    if (!sampleDocument) return;

    const recomputeDesktopPosition = () => {
      const [groupIdx, questionIdx] = sampleDocument.id.split("-").map(Number);
      const questionsCount =
        Object.values(groupedQuestions)[groupIdx]?.length ?? 0;
      const nextDesktopIdx = getRowEndIndex(
        groupIdx,
        questionIdx,
        questionsCount
      );

      setSampleDocument((prev) =>
        prev && prev.questionIdxDesktop !== nextDesktopIdx
          ? { ...prev, questionIdxDesktop: nextDesktopIdx }
          : prev
      );
    };

    let frame = 0;
    const onResize = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(recomputeDesktopPosition);
    };

    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(frame);
      window.removeEventListener("resize", onResize);
    };
  }, [sampleDocument, groupedQuestions, getRowEndIndex]);

  interface QuestionCaptionProps {
    question: PaxCheckHaveDocumentQuestion;
    id: string;
    currentSample: SampleDocument | null;
    onToggle: () => void;
  }

  const QuestionCaption: FC<QuestionCaptionProps> = ({
    question,
    id,
    currentSample,
    onToggle,
  }) => {
    return (
      <StyledQuestionCaption>
        <>
          <ButtonTitle>{capitalizeTitle(question.docTitle)}</ButtonTitle>
          {question.questionTitle.subtitle ? (
            <Body className="subtitle">
              {capitalizeTitle(question.questionTitle.subtitle)}
            </Body>
          ) : null}
        </>
        {question.sampleImage ? (
          <QuestionSampleHaveDocuments
            sampleImage={question.sampleImage}
            onToggle={onToggle}
            isToggleOpen={currentSample !== null && id === currentSample.id}
          />
        ) : null}
      </StyledQuestionCaption>
    );
  };

  return (
    <>
      {Object.entries(groupedQuestions).map(
        ([categoryTitle, categoryQuestions], groupIdx) => {
          return (
            <QuestionBox key={categoryTitle} $optionsCount={questions.length}>
              <Summary>{capitalizeTitle(categoryTitle)}</Summary>
              <section>
                {categoryQuestions.map((question, questionIdx) => {
                  const checked = answers.get(question.id) as boolean;

                  return (
                    <Fragment key={`question.id-${groupIdx}-${questionIdx}`}>
                      <HaveDocumentQuestion
                        ref={(el: HTMLDivElement | null) => {
                          cardRefs.current.set(
                            `${groupIdx}-${questionIdx}`,
                            el
                          );
                        }}
                      >
                        <>
                          {getImageForDocument(
                            question.country || "unsupported_country"
                          )}

                          <Checkbox
                            label={null}
                            checked={checked || false}
                            onChange={handleChange(question.id)}
                            error={null}
                          />
                        </>
                        <QuestionCaption
                          question={question}
                          id={`${groupIdx}-${questionIdx}`}
                          currentSample={sampleDocument}
                          onToggle={() => {
                            if (sampleDocument && sampleDocument.id === `${groupIdx}-${questionIdx}`) {
                              setSampleDocument(null);
                              return;
                            }

                            setSampleDocument({
                              id: `${groupIdx}-${questionIdx}`,
                              image: question.sampleImage || null,
                              groupIdx,
                              // Desktop: after the clicked card's whole row.
                              // Mobile (single column): right after the card.
                              questionIdxDesktop: getRowEndIndex(
                                groupIdx,
                                questionIdx,
                                categoryQuestions.length
                              ),
                              questionIdxMobile: questionIdx,
                            });
                          }}
                        />
                      </HaveDocumentQuestion>
                      {sampleDocument &&
                      sampleDocument.groupIdx === groupIdx &&
                      sampleDocument.questionIdxMobile === questionIdx ? (
                        <MobileDocSample>
                          <DocSampleBox
                            sampleDocument={sampleDocument}
                            onClose={() => {
                              setSampleDocument(null);
                            }}
                          />
                        </MobileDocSample>
                      ) : null}

                      {sampleDocument &&
                      sampleDocument.groupIdx === groupIdx &&
                      sampleDocument.questionIdxDesktop === questionIdx ? (
                        <DesktopDocSample>
                          <DocSampleBox
                            sampleDocument={sampleDocument}
                            onClose={() => {
                              setSampleDocument(null);
                            }}
                          />
                        </DesktopDocSample>
                      ) : null}
                    </Fragment>
                  );
                })}
              </section>
            </QuestionBox>
          );
        }
      )}
    </>
  );
};

export default HaveDocumentQuestionsPane;
